/**
 * Game Categories Configuration
 * Maps game IDs to 2-letter codes for encoding in market titles
 */

export interface GameCategory {
  id: string;
  code: string;
  name: string;
  fullName?: string;
}

export const GAME_CATEGORIES: GameCategory[] = [
  { id: "cs2", code: "CS", name: "Counter-Strike 2", fullName: "Counter-Strike 2" },
  { id: "dota2", code: "DT", name: "Dota 2", fullName: "Dota 2" },
  { id: "lol", code: "LL", name: "League of Legends", fullName: "League of Legends" },
  { id: "valorant", code: "VL", name: "Valorant", fullName: "Valorant" },
  { id: "fortnite", code: "FN", name: "Fortnite", fullName: "Fortnite" },
  { id: "overwatch", code: "OW", name: "Overwatch", fullName: "Overwatch" },
  { id: "apex", code: "AP", name: "Apex Legends", fullName: "Apex Legends" },
  { id: "rocketleague", code: "RL", name: "Rocket League", fullName: "Rocket League" },
  { id: "smash", code: "SM", name: "Super Smash Bros", fullName: "Super Smash Bros" },
  { id: "fgc", code: "FG", name: "Fighting Games", fullName: "Fighting Games" },
  { id: "mobile", code: "MB", name: "Mobile Esports", fullName: "Mobile Esports" },
  { id: "other", code: "OT", name: "Other", fullName: "Other" },
];

/**
 * Get game category by ID
 */
export function getGameByID(id: string): GameCategory | undefined {
  return GAME_CATEGORIES.find((game) => game.id === id);
}

/**
 * Get game category by 2-letter code
 */
export function getGameByCode(code: string): GameCategory | undefined {
  return GAME_CATEGORIES.find((game) => game.code === code.toUpperCase());
}

/**
 * Extract game code from market title
 * Format: "CS: Market title here" -> "CS"
 */
export function extractGameCode(title: string): string | null {
  const match = title.match(/^([A-Z]{2}):\s/);
  return match ? match[1] : null;
}

/**
 * Remove game code prefix from market title
 * Format: "CS: Market title here" -> "Market title here"
 */
export function removeGameCode(title: string): string {
  return title.replace(/^[A-Z]{2}:\s/, "");
}

/**
 * Add game code prefix to market title
 * Format: "Market title here" + "CS" -> "CS: Market title here"
 */
export function addGameCodeToTitle(title: string, gameId: string): string {
  const game = getGameByID(gameId);
  if (!game) return title;
  
  // Remove existing code if present
  const cleanTitle = removeGameCode(title);
  
  // Add new code
  return `${game.code}: ${cleanTitle}`;
}

/**
 * Get game info from market title
 */
export function getGameFromTitle(title: string): GameCategory | null {
  const code = extractGameCode(title);
  if (!code) return null;
  
  return getGameByCode(code) || null;
}

/**
 * Filter markets by game category
 */
export function filterMarketsByGame(markets: any[], gameId: string | null): any[] {
  if (!gameId || gameId === "all") return markets;
  
  const game = getGameByID(gameId);
  if (!game) return markets;
  
  return markets.filter((market) => {
    const marketGame = getGameFromTitle(market.question);
    return marketGame?.id === gameId;
  });
}

/**
 * Get game category from legacy category field (for backward compatibility)
 */
export function migrateCategory(legacyCategory?: string): string {
  if (!legacyCategory) return "other";
  
  // Map legacy categories to game IDs if they exist
  const game = GAME_CATEGORIES.find(
    (g) => g.id === legacyCategory.toLowerCase()
  );
  
  return game?.id || "other";
}

